/**
 * TanStack Query hooks for Otto Views.
 *
 * The GET can run a first-visit generation server-side (seconds, one bounded
 * LLM call), so the query result stays authoritative: `staleTime: Infinity` +
 * no refetch-on-mount — the *backend* owns staleness via the fingerprint and
 * reports it as `data.stale`. Regeneration and steer saves write their
 * responses straight into the cache.
 *
 * Cache identity is `(tenantId, definitionId, refs)` — the same Definition on
 * two scopes and two Definitions on one scope are distinct entries. An
 * undefined `definitionId` addresses the built-in Brief (`BUILTIN_DEFINITION`
 * key segment); mutation writes key off the RESPONSE's definitionId, which is
 * always the resolved concrete id.
 */

import {useCallback, useEffect, useRef, useState} from 'react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {useApiToken} from '@/matic/hooks/useApiClient';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {trackProductEvent} from '@/matic/lib/product-observability';
import {
  getViewApi,
  type ViewBlockEntryInput,
  type ViewStreamProgress,
} from '../api/view-api';
import type {
  BriefRefTarget,
  ViewBlock,
  ViewDefinition,
  ViewInstanceSummary,
  ViewResult,
  ViewSteer,
  ViewStatus,
  ViewTemplate,
} from '../types';

/**
 * The built-in Brief's well-known id (mirrors the backend's
 * BRIEF_DEFINITION_ID). Used to key the builtin's cache entries identically
 * whether it was addressed implicitly (no definitionId) or explicitly.
 */
export const BUILTIN_DEFINITION_ID = '00000000-0000-0000-0000-000000000b12';

/**
 * Well-known id of the built-in "Portfolio" Definition (`scope_mode=type`) —
 * mirrors PORTFOLIO_DEFINITION_ID in builtin_portfolio.py. Addressed explicitly
 * for a type-wide aggregate View (`?ref=type:{typeId}`).
 */
export const PORTFOLIO_DEFINITION_ID = '00000000-0000-0000-0000-000000000b13';

export const viewKeys = {
  all: ['views'] as const,
  /** Prefix over every rendered-View entry (all scopes × definitions). */
  views: () => [...viewKeys.all, 'view'] as const,
  view: (tenantId: string, definitionId: string, refs: string[]) =>
    [...viewKeys.all, 'view', tenantId, definitionId, ...refs] as const,
  statuses: () => [...viewKeys.all, 'status'] as const,
  steer: (tenantId: string, definitionId: string, refs: string[]) =>
    [...viewKeys.all, 'steer', tenantId, definitionId, ...refs] as const,
  status: (tenantId: string, definitionId: string, refs: string[]) =>
    [...viewKeys.all, 'status', tenantId, definitionId, ...refs] as const,
  instances: (tenantId: string) =>
    [...viewKeys.all, 'instances', tenantId] as const,
  /**
   * `scopeKey` omitted = the unscoped "browse everything" catalog — and, as
   * a bare `(tenantId)` call, a genuine array-prefix of every scoped key
   * below it, so `invalidateQueries({queryKey: viewKeys.definitions(tenantId)})`
   * (default `exact: false`) refetches scoped catalogs too.
   */
  definitions: (tenantId: string, scopeKey?: string) =>
    scopeKey
      ? ([...viewKeys.all, 'definitions', tenantId, scopeKey] as const)
      : ([...viewKeys.all, 'definitions', tenantId] as const),
  templates: (tenantId: string) =>
    [...viewKeys.all, 'templates', tenantId] as const,
};

/** Normalize client-side the same way the backend does (sorted + deduped). */
export function normalizeScopeRefs(refs: string[]): string[] {
  return [...new Set(refs)].sort();
}

/**
 * Mirrors the backend's scope-key rule exactly (`scope_key.normalize_scope_
 * key` / `aggregate_scope_key`, `views/application/use_cases/scope_key.py`):
 * a scope of exactly one Category ref (`typeId:*`) is the type-wide
 * aggregate form and keys as `type:{typeId}`; everything else — a lone Tag,
 * or a multi-ref scope (mixed Category × Tag included) — keys as the
 * deduped, sorted, comma-joined ref list, wildcards spelled `typeId:*`
 * in place. The two spellings a caller might send for a lone Category
 * (`typeId:*` from the URL, or `type:{typeId}` from aggregate-mode's
 * `refStrings`) converge on the same key here, exactly as they do server-side.
 *
 * Diverging from the backend's rule — even just in a client-side "already
 * generated for this scope" badge — writes a slow regenerate into the wrong
 * cache entry when the mismatched key is later used for mutation lookups.
 */
export function normalizeScopeKey(refs: string[]): string {
  const normalized = normalizeScopeRefs(refs);
  if (normalized.length === 1 && normalized[0].endsWith(':*')) {
    return `type:${normalized[0].slice(0, -2)}`;
  }

  return normalized.join(',');
}

function definitionKeySegment(definitionId?: string): string {
  return definitionId ?? BUILTIN_DEFINITION_ID;
}

/**
 * The one definition of a rendered-View query — key, fetcher, and caching
 * policy — shared by the canvas's `useViewQuery` and the index's preview
 * fan-out (`useViewPreviews`). They MUST stay byte-identical: a preview writes
 * the same cache entry the canvas later reads (that's what makes opening a
 * previewed View instant), and any drift here would split them into two
 * entries and double the GETs — each of which can be a generation.
 */
function viewQueryOptions(
  token: string | undefined,
  tenantId: string,
  normalizedRefs: string[],
  definitionId?: string
) {
  return {
    queryKey: viewKeys.view(
      tenantId,
      definitionKeySegment(definitionId),
      normalizedRefs
    ),
    queryFn: async () =>
      getViewApi(token).getView(normalizedRefs, definitionId),
    // The backend fingerprint (data.stale) is the freshness signal, not the
    // TanStack clock — a refetch re-runs the (possibly generating) GET.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  };
}

export function useViewQuery(
  refs: string[],
  definitionId?: string,
  options: {enabled?: boolean} = {}
): UseQueryResult<ViewResult> {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const normalized = normalizeScopeRefs(refs);
  return useQuery({
    ...viewQueryOptions(token, tenantId, normalized, definitionId),
    enabled:
      Boolean(token) && normalized.length > 0 && (options.enabled ?? true),
  });
}

/**
 * Fan-out of rendered-View reads for the index's preview cards, keyed by
 * `definitionId ?? BUILTIN_DEFINITION_ID`.
 *
 * DANGER: `GET /views` **generates on cache miss** (10–30s of LLM). A preview
 * must never be the thing that triggers a generation, so `enabled` is the
 * caller's responsibility and the caller must gate it on the definition
 * already having an artifact for this scope
 * (`ViewCatalogItem.generatedForCurrentScope`). Passing `enabled: true` for an
 * ungenerated Definition silently kicks off a real generation from a
 * thumbnail.
 *
 * Because the query options are shared with `useViewQuery`, a resolved preview
 * doubles as a prefetch: opening that View is a cache hit.
 */
export function useViewPreviews(
  refs: string[],
  entries: Array<{definitionId?: string; enabled: boolean}>
): Map<string, ViewResult> {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const normalized = normalizeScopeRefs(refs);
  const results = useQueries({
    queries: entries.map((entry) => ({
      ...viewQueryOptions(token, tenantId, normalized, entry.definitionId),
      enabled: Boolean(token) && normalized.length > 0 && entry.enabled,
    })),
  });

  const previews = new Map<string, ViewResult>();
  for (const [index, entry] of entries.entries()) {
    const data = results[index]?.data;
    if (data) {
      previews.set(definitionKeySegment(entry.definitionId), data);
    }
  }

  return previews;
}

export function useViewStatusQuery(
  refs: string[],
  definitionId?: string,
  options: {enabled?: boolean} = {}
): UseQueryResult<ViewStatus> {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const normalized = normalizeScopeRefs(refs);
  return useQuery({
    queryKey: viewKeys.status(
      tenantId,
      definitionKeySegment(definitionId),
      normalized
    ),
    queryFn: async () => getViewApi(token).getStatus(normalized, definitionId),
    enabled:
      Boolean(token) && normalized.length > 0 && (options.enabled ?? true),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useViewInstances(): UseQueryResult<ViewInstanceSummary[]> {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: viewKeys.instances(tenantId),
    queryFn: async () => getViewApi(token).getInstances(),
    enabled: Boolean(token),
    staleTime: 30 * 1000,
  });
}

/**
 * The server's `scope_key` ("t:r,t:r", already normalized) + `definitionId`
 * are the authority for which cache entry a mutation result belongs to.
 * Hook-level `onSuccess` closures re-bind to the LATEST render (TanStack v5),
 * so keying off the render-scope `refs` would write a slow response into
 * whatever scope the user is looking at by the time it resolves — with
 * `staleTime: Infinity`, permanently. Deriving the key from the response
 * makes that race harmless.
 */
function refsFromScopeKey(scopeKey: string): string[] {
  return scopeKey.split(',');
}

/**
 * Write a generated View into the cache: the View entry plus the derived status
 * entry for the same instance, then refresh the instance catalog. Keys off the
 * RESPONSE's identity (refsFromScopeKey + `data.definitionId`), never the
 * render-scope refs — see the note above `refsFromScopeKey`. Shared by the full
 * regenerate and the per-block refill (which returns the merged artifact, so
 * the write is identical).
 */
export function writeViewToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  tenantId: string,
  data: ViewResult
): void {
  const responseRefs = refsFromScopeKey(data.scopeKey);
  queryClient.setQueryData(
    viewKeys.view(tenantId, data.definitionId, responseRefs),
    data
  );
  queryClient.setQueryData<ViewStatus>(
    viewKeys.status(tenantId, data.definitionId, responseRefs),
    {
      scopeKey: data.scopeKey,
      definitionId: data.definitionId,
      definitionName: data.definitionName,
      generated: true,
      stale: data.stale,
      generatedAt: data.generatedAt,
      steerUpdatedAt: data.steerUpdatedAt,
    }
  );
  void queryClient.invalidateQueries({
    queryKey: viewKeys.instances(tenantId),
  });
}

export function useRegenerateView(
  refs: string[],
  definitionId?: string
): UseMutationResult<ViewResult, Error, void> {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const queryClient = useQueryClient();
  const normalized = normalizeScopeRefs(refs);
  return useMutation({
    mutationFn: async () =>
      getViewApi(token).regenerate(normalized, definitionId),
    onSuccess(data) {
      writeViewToCache(queryClient, tenantId, data);
      trackProductEvent('resource.updated', {
        resourceType: 'view_instance',
        definitionId: data.definitionId,
        scopeKey: data.scopeKey,
        stale: data.stale,
        trigger: 'regenerate',
      });
    },
  });
}

/** What the canvas needs to render a generation in progress. */
export type ViewGenerationStream = {
  isStreaming: boolean;
  /** The Definition's block shape, once the server has echoed it. */
  progress?: ViewStreamProgress;
  /** Validated blocks that have arrived, in the order the model closed them. */
  blocks: ViewBlock[];
  /** Citation targets accumulated from streamed blocks, so markers resolve
   *  before the terminal `complete` event lands (merge with the cached
   *  View's `refs` for a regenerate, which already has its own). */
  refs: Record<string, BriefRefTarget>;
  error?: string;
  /** Begin a generation. `force` replaces an existing View (writer-only). */
  start: (options?: {force?: boolean}) => void;
  /** Stop this generation. The disconnect cancels it server-side too, and
   *  nothing is persisted — so the previously cached View is what's left. */
  cancel: () => void;
  /** Clear a finished/failed run's residue before starting another. */
  reset: () => void;
};

/**
 * Stream a View generation, surfacing blocks as they compose.
 *
 * The terminal `complete` event carries the authoritative persisted View and is
 * written into the SAME cache entry `useRegenerateView` writes — keyed off the
 * response's own `scopeKey`/`definitionId`, for the reason documented above
 * `refsFromScopeKey`. Streamed blocks are visual only: they are never merged
 * into the cache, so the artifact a reader ends up with is always the one the
 * server validated as a whole.
 *
 * Aborting the stream cancels the generation server-side as well: the
 * controller only persists from its terminal branch, so a disconnect leaves the
 * previously persisted artifact untouched. That is what `cancel()` — the Stop
 * button — relies on, and it applies equally to the abort on unmount, so
 * navigating away mid-compose discards the work rather than finishing it
 * headless. The tokens already spent are still billed either way.
 */
export function useStreamingViewGeneration(
  refs: string[],
  definitionId?: string
): ViewGenerationStream {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const queryClient = useQueryClient();
  const normalized = normalizeScopeRefs(refs);
  const [isStreaming, setIsStreaming] = useState(false);
  const [progress, setProgress] = useState<ViewStreamProgress | undefined>(
    undefined
  );
  const [blocks, setBlocks] = useState<ViewBlock[]>([]);
  const [citationRefs, setCitationRefs] = useState<
    Record<string, BriefRefTarget>
  >({});
  const [error, setError] = useState<string | undefined>(undefined);
  const abortRef = useRef<(() => void) | undefined>(undefined);

  // Read inside the callback rather than closing over the render's value, so a
  // scope switch mid-stream can't write the finishing generation into the
  // wrong tenant's cache.
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;

  // Bumped on every start()/reset()/cancel() and captured per-generation, so a
  // generation superseded mid-flight (switched scope, cancelled, restarted)
  // can still finish writing its result into the cache (onComplete/onError
  // always run) but can no longer mutate isStreaming/abortRef/progress/blocks
  // out from under whichever generation owns them now.
  const generationIdRef = useRef(0);

  const reset = useCallback(() => {
    // Deliberately does NOT call abortRef.current(): unlike cancel(), this is
    // "clear the residue", not "stop the work" — an in-flight generation is
    // left running so it finishes and persists (aborting it would discard it;
    // see the hook doc comment below). But it must no
    // longer be treated as "in flight" for THIS hook instance, or a caller
    // switching to a new scope/instance right after reset() would find
    // start() a permanent no-op (abortRef still set) with isStreaming stuck
    // true and nothing left to ever clear it.
    generationIdRef.current += 1;
    abortRef.current = undefined;
    setIsStreaming(false);
    setProgress(undefined);
    setBlocks([]);
    setCitationRefs({});
    setError(undefined);
  }, []);

  /** Stop an in-flight generation and clear its residue, so the canvas falls
   *  back to the cached View — which the server never overwrote, because the
   *  abort reaches it before the persist (see the hook doc comment). */
  const cancel = useCallback(() => {
    generationIdRef.current += 1;
    abortRef.current?.();
    abortRef.current = undefined;
    setIsStreaming(false);
    setProgress(undefined);
    setBlocks([]);
    setCitationRefs({});
    setError(undefined);
  }, []);

  const start = useCallback(
    (options?: {force?: boolean}) => {
      if (abortRef.current) return; // One generation in flight at a time.
      generationIdRef.current += 1;
      const myGenerationId = generationIdRef.current;
      setIsStreaming(true);
      setProgress(undefined);
      setBlocks([]);
      setCitationRefs({});
      setError(undefined);
      abortRef.current = getViewApi(token).streamView(
        normalized,
        definitionId,
        {
          onProgress(next) {
            if (generationIdRef.current !== myGenerationId) return;
            setProgress(next);
          },
          onBlock(block, blockRefs) {
            if (generationIdRef.current !== myGenerationId) return;
            // Replace-by-id rather than append: a block the server re-sends
            // (or a retry that revisits one) must not render twice.
            setBlocks((current) => [
              ...current.filter((item) => item.block_id !== block.block_id),
              block,
            ]);
            setCitationRefs((current) => ({...current, ...blockRefs}));
          },
          onComplete(result) {
            // Always write the completed generation to the cache, even if a
            // newer generation has since superseded this one in the UI — the
            // result is still correct and worth keeping (see the hook doc
            // comment on why reset() doesn't abort in-flight work).
            //
            // This is also deliberately unguarded against a Stop pressed while
            // a `complete` frame was already in flight: the server having
            // emitted `complete` means it also persisted, so showing the new
            // View is the honest thing. A race guard here would show the old
            // View over a DB row that no longer matches it.
            writeViewToCache(queryClient, tenantRef.current, result);
            trackProductEvent('resource.updated', {
              resourceType: 'view_instance',
              definitionId: result.definitionId,
              scopeKey: result.scopeKey,
              stale: result.stale,
              trigger: 'stream_generate',
            });
            if (generationIdRef.current !== myGenerationId) return;
            abortRef.current = undefined;
            setIsStreaming(false);
          },
          onError(message) {
            if (generationIdRef.current !== myGenerationId) return;
            setError(message);
            abortRef.current = undefined;
            setIsStreaming(false);
          },
        },
        {force: options?.force ?? false}
      );
    },
    [token, normalized, definitionId, queryClient]
  );

  useEffect(
    () => () => {
      abortRef.current?.();
    },
    []
  );

  return {
    isStreaming,
    progress,
    blocks,
    refs: citationRefs,
    error,
    start,
    cancel,
    reset,
  };
}

/**
 * Refill one block. The response is the MERGED artifact (whole View, one
 * block's content replaced), so it lands in exactly the same cache entry a
 * full regenerate writes — no per-block cache surgery.
 */
export function useRegenerateViewBlock(
  refs: string[],
  definitionId?: string
): UseMutationResult<ViewResult, Error, string> {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const queryClient = useQueryClient();
  const normalized = normalizeScopeRefs(refs);
  return useMutation({
    async mutationFn(blockId: string) {
      return getViewApi(token).regenerateBlock(
        normalized,
        blockId,
        definitionId
      );
    },
    onSuccess(data, blockId) {
      writeViewToCache(queryClient, tenantId, data);
      trackProductEvent('resource.updated', {
        resourceType: 'view_block',
        definitionId: data.definitionId,
        scopeKey: data.scopeKey,
        blockId,
        trigger: 'regenerate_block',
      });
    },
  });
}

export function useViewSteerQuery(
  refs: string[],
  definitionId?: string,
  options: {enabled?: boolean} = {}
): UseQueryResult<ViewSteer> {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const normalized = normalizeScopeRefs(refs);
  return useQuery({
    queryKey: viewKeys.steer(
      tenantId,
      definitionKeySegment(definitionId),
      normalized
    ),
    queryFn: async () => getViewApi(token).getSteer(normalized, definitionId),
    enabled:
      Boolean(token) && normalized.length > 0 && (options.enabled ?? true),
  });
}

/**
 * Write a saved steer into the cache: the steer entry itself, plus the
 * stale-flag + steerUpdatedAt on the cached View/status for the same instance.
 * Keys off the RESPONSE's identity (refsFromScopeKey), never the render-scope
 * refs — a slow save must never land under another instance. Shared by the
 * `useUpdateViewSteer` REST mutation and the builder rail's `view_steer_updated`
 * SSE handler (both are response/event-keyed writes).
 */
export function writeSteerToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  tenantId: string,
  steer: ViewSteer
): void {
  const responseRefs = refsFromScopeKey(steer.scopeKey);
  queryClient.setQueryData(
    viewKeys.steer(tenantId, steer.definitionId, responseRefs),
    steer
  );
  // The saved steer changes the backend fingerprint: reflect staleness on the
  // cached View immediately without refetching (a refetch could trigger nothing
  // anyway — GET never regenerates an existing View).
  queryClient.setQueryData<ViewResult | undefined>(
    viewKeys.view(tenantId, steer.definitionId, responseRefs),
    (current) =>
      current
        ? {...current, stale: true, steerUpdatedAt: steer.updatedAt}
        : current
  );
  queryClient.setQueryData<ViewStatus | undefined>(
    viewKeys.status(tenantId, steer.definitionId, responseRefs),
    (current) =>
      current
        ? {...current, stale: true, steerUpdatedAt: steer.updatedAt}
        : current
  );
}

export function useUpdateViewSteer(
  refs: string[],
  definitionId?: string
): UseMutationResult<ViewSteer, Error, string> {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const queryClient = useQueryClient();
  const normalized = normalizeScopeRefs(refs);
  return useMutation({
    async mutationFn(instructions: string) {
      return getViewApi(token).updateSteer(
        normalized,
        instructions,
        definitionId
      );
    },
    onSuccess(data) {
      writeSteerToCache(queryClient, tenantId, data);
      trackProductEvent('resource.updated', {
        resourceType: 'view_steer',
        definitionId: data.definitionId,
        scopeKey: data.scopeKey,
        trigger: 'steer_saved',
      });
    },
  });
}

/** The Definition gallery (built-in Brief first, then saved Definitions). */
/**
 * The Definition catalog. `scope` omitted lists every Definition regardless
 * of origin (unscoped browse — `LibraryGlobalSearchDialog`, `LibraryBrowseHub`,
 * `useHomeActivityDigest`, and `ViewsIndex`'s own no-scope-picked mode all
 * call this bare). When present, a custom Definition not created in this
 * scope (and not shared/legacy) is server-filtered out — see
 * `ListViewDefinitionsUseCase`.
 */
export function useViewDefinitions(
  scope?: {refs: string[]} | {aggregateTypeId: string}
): UseQueryResult<ViewDefinition[]> {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const scopeKey =
    scope && 'aggregateTypeId' in scope
      ? `type:${scope.aggregateTypeId}`
      : scope
        ? normalizeScopeKey(scope.refs)
        : undefined;
  return useQuery({
    queryKey: viewKeys.definitions(tenantId, scopeKey),
    queryFn: async () =>
      getViewApi(token).getDefinitions(
        scope && 'aggregateTypeId' in scope
          ? {aggregateTypeId: scope.aggregateTypeId}
          : scope
            ? {refs: scope.refs}
            : undefined
      ),
    enabled: Boolean(token),
  });
}

/** Stock templates — static server-side, so cached indefinitely. */
export function useViewTemplates(
  options: {enabled?: boolean} = {}
): UseQueryResult<ViewTemplate[]> {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  return useQuery({
    queryKey: viewKeys.templates(tenantId),
    queryFn: async () => getViewApi(token).getTemplates(),
    enabled: Boolean(token) && (options.enabled ?? true),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * Merge one Definition into the gallery cache — update the matching row or
 * append. Shared by the mutation hooks below and the builder rail's
 * `view_definition_updated` SSE handler (both are response/event-keyed writes).
 */
export function upsertDefinitionInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  tenantId: string,
  definition: ViewDefinition
): void {
  queryClient.setQueryData<ViewDefinition[]>(
    viewKeys.definitions(tenantId),
    (current) => {
      if (!current) return [definition];
      const index = current.findIndex((d) => d.id === definition.id);
      if (index === -1) return [...current, definition];
      const next = [...current];
      next[index] = definition;
      return next;
    }
  );
}

export type UpdateDefinitionVariables = {
  definitionId: string;
  name?: string;
  blocks?: ViewBlockEntryInput[];
  /** Promote (true) or un-promote (false) as a cross-scope template. */
  isShared?: boolean;
};

/**
 * Definition CRUD (create / update / archive). Response-keyed cache writes
 * into the gallery; every write also invalidates the rendered-View subtree so
 * the backend's fingerprint-driven `stale` chip surfaces on the next read
 * (active canvases refetch — GET never regenerates an existing View).
 */
export function useViewDefinitionMutations(): {
  createMutation: UseMutationResult<
    ViewDefinition,
    Error,
    {
      name: string;
      blocks?: ViewBlockEntryInput[];
      scopeMode?: 'records' | 'type';
      /** Stamps the new Definition's `originScopeKey` (see `useViewDefinitions`). */
      scope?: {refs?: string[]; aggregateTypeId?: string};
    }
  >;
  updateMutation: UseMutationResult<
    ViewDefinition,
    Error,
    UpdateDefinitionVariables,
    {previous: Array<[readonly unknown[], ViewDefinition[] | undefined]>}
  >;
  archiveMutation: UseMutationResult<void, Error, string>;
} {
  const tenantId = useRequiredTenantId();
  const token = useApiToken();
  const queryClient = useQueryClient();

  // Broad, prefix-matched invalidation (default `exact: false`) so an active
  // SCOPED catalog (useViewsTabController's useViewDefinitions(scope)) picks
  // up create/update/archive too — the response-keyed setQueryData writes
  // below only ever target the unscoped cache entry.
  const invalidateAllDefinitionCatalogs = () => {
    void queryClient.invalidateQueries({
      queryKey: viewKeys.definitions(tenantId),
    });
  };

  const createMutation = useMutation({
    async mutationFn(input: {
      name: string;
      blocks?: ViewBlockEntryInput[];
      scopeMode?: 'records' | 'type';
      scope?: {refs?: string[]; aggregateTypeId?: string};
    }) {
      return getViewApi(token).createDefinition(
        input.name,
        input.blocks,
        input.scopeMode,
        input.scope
      );
    },
    onSuccess(definition) {
      upsertDefinitionInCache(queryClient, tenantId, definition);
      invalidateAllDefinitionCatalogs();
      trackProductEvent('resource.created', {
        resourceType: 'view_definition',
        definitionId: definition.id,
        name: definition.name,
        scopeMode: definition.scopeMode,
      });
    },
  });

  const updateMutation = useMutation({
    async mutationFn(variables: UpdateDefinitionVariables) {
      return getViewApi(token).updateDefinition(variables.definitionId, {
        name: variables.name,
        blocks: variables.blocks,
        isShared: variables.isShared,
      });
    },
    // Optimistic gallery write so direct manipulation (reorder, remove,
    // block edit) reflects instantly; server ids reconcile on success. Writes
    // every matching cache entry (default `exact: false`) — unscoped AND any
    // active scoped catalog (`useViewsTabController`'s `useViewDefinitions(scope)`)
    // — not just the unscoped one, or a scoped canvas would see neither the
    // optimistic update nor its rollback.
    async onMutate(variables) {
      await queryClient.cancelQueries({
        queryKey: viewKeys.definitions(tenantId),
      });
      const previous = queryClient.getQueriesData<ViewDefinition[]>({
        queryKey: viewKeys.definitions(tenantId),
      });
      if (variables.blocks) {
        const optimisticBlocks = variables.blocks.map((entry, index) => ({
          ...entry,
          id: entry.id ?? `optimistic-${index}`,
        }));
        queryClient.setQueriesData<ViewDefinition[]>(
          {queryKey: viewKeys.definitions(tenantId)},
          (current) =>
            current?.map((d) =>
              d.id === variables.definitionId
                ? {
                    ...d,
                    ...(variables.name === undefined
                      ? {}
                      : {name: variables.name}),
                    blocks: optimisticBlocks,
                  }
                : d
            )
        );
      }

      return {previous};
    },
    onError(_error, _variables, context) {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess(definition, variables) {
      upsertDefinitionInCache(queryClient, tenantId, definition);
      invalidateAllDefinitionCatalogs();
      // The edited Definition changes the backend fingerprint — surface the
      // stale chip on any open canvas rendering it.
      void queryClient.invalidateQueries({queryKey: viewKeys.views()});
      void queryClient.invalidateQueries({queryKey: viewKeys.statuses()});
      trackProductEvent('resource.updated', {
        resourceType: 'view_definition',
        definitionId: definition.id,
        fieldsChanged: [
          ...(variables.name === undefined ? [] : ['name']),
          ...(variables.blocks === undefined ? [] : ['blocks']),
          ...(variables.isShared === undefined ? [] : ['isShared']),
        ],
      });
    },
  });

  const archiveMutation = useMutation({
    async mutationFn(definitionId: string) {
      await getViewApi(token).archiveDefinition(definitionId);
    },
    onSuccess(_data, definitionId) {
      queryClient.setQueryData<ViewDefinition[]>(
        viewKeys.definitions(tenantId),
        (current) => current?.filter((d) => d.id !== definitionId)
      );
      invalidateAllDefinitionCatalogs();
      trackProductEvent('resource.updated', {
        resourceType: 'view_definition',
        definitionId,
        archived: true,
      });
    },
  });

  return {createMutation, updateMutation, archiveMutation};
}
